import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	selectContactNumberTypes,
	getContactNumberTypesAsync,
	getContactNumberTypeAsync,
	createContactNumberTypeAsync,
	selectContactNumberType,
	selectEditingContactNumberType,
	setEditingContactNumberType,
	removeContactNumberType,
	updateContactNumberTypeAsync,
} from '../../redux/contactNumberTypeSlice';
import { selectCurrentTab, updateTabStatus } from '../../redux/tabsSlice';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Box, Checkbox, FormControlLabel, TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { blueGrey, green, orange } from '@mui/material/colors';
import CancelIcon from '@mui/icons-material/Cancel';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { Grid } from '@mui/material';
import { createAlertAsync } from '../../redux/alertsSlice';
import CircularProgress from '@mui/material/CircularProgress';
import _ from 'lodash';
import SaveIcon from '@mui/icons-material/Save';
import Backdrop from '@mui/material/Backdrop';
import CreatedModified from '../shared/CreatedModified';

export default function ContactNumberTypes() {
	const dispatch = useDispatch();
	const currentTab = useSelector(selectCurrentTab);
	const contactNumberTypes = useSelector(selectContactNumberTypes);
	const editing = useSelector(selectEditingContactNumberType());
	const contactNumberType = useSelector(selectContactNumberType());
	const [name, setName] = useState('');
	const [validNumber, setValidNumber] = useState(false);
	const [addModalOpen, setAddModal] = useState(false);
	const [dialogueModalOpen, setDialogueModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const handleAddModal = () => setAddModal(!addModalOpen);
	const handleDialogueModal = () => setDialogueModal(!dialogueModalOpen);

	useEffect(() => {
		if (!contactNumberTypes) {
			dispatch(getContactNumberTypesAsync());
		}
	}, [contactNumberTypes, dispatch]);

	useEffect(() => {
		if (_.isEqual(editing, contactNumberType)) {
			dispatch(updateTabStatus({ id: currentTab.id, status: true }));
		} else {
			dispatch(updateTabStatus({ id: currentTab.id, status: false }));
		}
	}, [currentTab, editing, contactNumberType, dispatch]);

	useEffect(() => {
		if (editing) {
			setLoading(false);
		}
	}, [editing, contactNumberTypes]);

	function handleEdit(event) {
		dispatch(
			setEditingContactNumberType({
				name: event.target.name,
				value: event.target.value,
				checked: event.target.checked,
			})
		);
	}

	function addContactNumberType(e) {
		e.preventDefault();

		const exitingContactNumberType = contactNumberTypes.find(
			(contactNumberType) => contactNumberType.Name === name
		);

		if (!exitingContactNumberType) {
			dispatch(createContactNumberTypeAsync(name, validNumber));
			handleAddModal();
		} else {
			dispatch(
				createAlertAsync({
					message: 'Contact Number Type already exists',
					severity: 'error',
					autoDismiss: true,
					timeout: 6000,
				})
			);
		}
	}

	function getContactNumberType(id) {
		if (!editing || (editing && _.isEqual(contactNumberType, editing))) {
			dispatch(removeContactNumberType());
			setLoading(true);
			dispatch(getContactNumberTypeAsync(id));
		} else {
			handleDialogueModal();
		}
	}

	function stopEditing() {
		if (_.isEqual(contactNumberType, editing)) {
			setLoading(false);
			dispatch(removeContactNumberType(editing._id));
		} else {
			handleDialogueModal();
		}
	}

	function updateContactNumberType(e) {
		e.preventDefault();
		setLoading(true);
		dispatch(updateContactNumberTypeAsync(editing));
	}

	function discardChanges() {
		setLoading(false);
		dispatch(removeContactNumberType());
		handleDialogueModal();
	}

	let saveColor = _.isEqual(contactNumberType, editing)
		? green[800]
		: orange[800];

	return (
		<Box>
			<Box sx={{ textAlign: 'right', marginBottom: 2, marginRight: 1 }}>
				<Tooltip title='Add Contact Number Type'>
					<IconButton onClick={handleAddModal}>
						<AddCircleIcon
							sx={{
								color: blueGrey[600],
								backgroundColor: blueGrey[100],
								borderRadius: '5px',
								fontSize: '1.5em',
							}}
						/>
					</IconButton>
				</Tooltip>
			</Box>
			<Box
				sx={{
					display: 'flex',
				}}
			>
				<Box
					sx={{
						flexGrow: 1,
						backgroundColor: blueGrey[100],
						color: blueGrey[900],
						borderRadius: '5px',
						margin: 2,
						paddingTop: 4,
						paddingBottom: 4,
						textAlign: 'center',
						alignItems: 'center',
						width: '15em',
						minWidth: '15em',
						maxWidth: '15em',
						minHeight: '400px',
					}}
				>
					{contactNumberTypes && (
						<Box>
							<Box sx={{ color: blueGrey[900], marginBottom: 1 }}>ACTIVE</Box>
							{contactNumberTypes.map(
								(contactNumberType) =>
									contactNumberType.IsActive === true && (
										<Box
											key={contactNumberType._id}
											sx={{ textAlign: 'center' }}
										>
											<Button
												variant='contained'
												onClick={() =>
													getContactNumberType(contactNumberType._id)
												}
												sx={{
													minWidth: '10em',
													textTransform: 'capitalize',
													marginBottom: 2,
													backgroundColor: contactNumberType.IsActive
														? blueGrey[800]
														: blueGrey[500],
													color: contactNumberType.IsActive
														? blueGrey[50]
														: blueGrey[900],
													'&:hover': {
														backgroundColor: blueGrey[900],
													},
												}}
											>
												{contactNumberType.Name}
											</Button>
										</Box>
									)
							)}
						</Box>
					)}
					{contactNumberTypes && (
						<Box>
							<Box sx={{ color: blueGrey[900], marginTop: 4, marginBottom: 1 }}>
								INACTIVE
							</Box>
							{contactNumberTypes.map(
								(contactNumberType) =>
									contactNumberType.IsActive === false && (
										<Box
											key={contactNumberType._id}
											sx={{ textAlign: 'center' }}
										>
											<Button
												variant='contained'
												onClick={() =>
													getContactNumberType(contactNumberType._id)
												}
												sx={{
													minWidth: '10em',
													textTransform: 'capitalize',
													marginBottom: 2,
													backgroundColor: contactNumberType.IsActive
														? blueGrey[800]
														: blueGrey[500],
													color: contactNumberType.IsActive
														? blueGrey[50]
														: blueGrey[900],
													'&:hover': {
														backgroundColor: blueGrey[900],
													},
												}}
											>
												{contactNumberType.Name}
											</Button>
										</Box>
									)
							)}
						</Box>
					)}
					{!contactNumberTypes && (
						<Box>
							<CircularProgress />
						</Box>
					)}
				</Box>
				<Box
					sx={{
						flexGrow: 8,
						backgroundColor: blueGrey[100],
						color: blueGrey[900],
						borderRadius: '10px',
						margin: 2,
						paddingTop: 2,
						paddingBottom: 2,
						visibility: `${editing || loading ? 'visible' : 'hidden'}`,
						minWidth: '500px',
					}}
				>
					{editing && !loading ? (
						<>
							<Box
								component={'form'}
								onSubmit={updateContactNumberType}
								sx={{ textAlign: 'right', marginRight: 4 }}
							>
								<Tooltip title='Save Contact Number Type'>
									<IconButton
										onClick={updateContactNumberType}
										sx={{ marginRight: 2 }}
									>
										<SaveIcon
											style={{
												color: saveColor,
												borderRadius: '5px',
												fontSize: '1.5em',
											}}
										/>
									</IconButton>
								</Tooltip>
								<Tooltip title='Close'>
									<IconButton onClick={stopEditing}>
										<CancelIcon
											sx={{
												color: blueGrey[600],
												borderRadius: '5px',
												fontSize: '1.5em',
											}}
										/>
									</IconButton>
								</Tooltip>
							</Box>
							<Grid
								container
								spacing={2}
								sx={{ marginTop: 6 }}
							>
								<Grid
									sm={6}
									item
								>
									<TextField
										key='IsActive'
										select
										required
										variant='standard'
										label='Is Active'
										value={editing.IsActive}
										name='IsActive'
										onChange={handleEdit}
										sx={{ width: '50%' }}
									>
										<MenuItem value={true}>TRUE</MenuItem>
										<MenuItem value={false}>FALSE</MenuItem>
									</TextField>
								</Grid>
								<Grid
									sm={6}
									item
								>
									<TextField
										key='Name'
										required
										variant='standard'
										label='Contact Number Type Name'
										value={editing.Name}
										name='Name'
										onChange={handleEdit}
										sx={{
											width: '80%',
											'& .MuiInputBase-input': {
												textAlign: 'center',
											},
										}}
									/>
								</Grid>
								<Grid
									sm={12}
									item
								>
									<FormControlLabel
										control={
											<Checkbox
												name='ValidNumber'
												checked={editing.ValidNumber}
												onChange={handleEdit}
											/>
										}
										label='Requires Valid Phone Number'
									/>
								</Grid>
							</Grid>
							<CreatedModified account={editing} />
						</>
					) : (
						<Box
							sx={{
								textAlign: 'center',
								alignItems: 'center',
								marginTop: 15,
								flexGrow: 20,
							}}
						>
							<CircularProgress
								size={60}
								color='primary'
								sx={{
									color: `${blueGrey[800]} !important`,
								}}
							/>
						</Box>
					)}
				</Box>
			</Box>
			<Modal
				open={addModalOpen}
				closeAfterTransition
				BackdropComponent={Backdrop}
				BackdropProps={{
					timeout: 1000,
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: blueGrey[50],
						border: '2px solid #000',
						boxShadow: 24,
						p: 4,
					}}
				>
					<Grid
						container
						component={'form'}
						onSubmit={addContactNumberType}
					>
						<Grid
							item
							xs={12}
						>
							<TextField
								key='name'
								autoFocus
								required
								variant='standard'
								label='Contact Number Type Name'
								value={name}
								name='name'
								onChange={(e) => setName(e.target.value)}
								//sx={textFieldStyles}
							/>
						</Grid>
						<Grid
							item
							xs={12}
							sx={{ marginTop: 4 }}
						>
							<FormControlLabel
								control={
									<Checkbox
										name='ValidNumber'
										value={validNumber}
										onChange={(e) => setValidNumber(e.target.checked)}
									/>
								}
								label='Requires Valid Phone Number'
							/>
						</Grid>

						<Stack
							spacing={2}
							direction='row'
							sx={{ marginTop: '3em' }}
						>
							<Button
								variant='contained'
								onClick={handleAddModal}
								sx={{
									flexGrow: 1,
									backgroundColor: blueGrey[500],
									'&:hover': { backgroundColor: blueGrey[600] },
								}}
							>
								Cancel
							</Button>
							<Button
								variant='contained'
								type='submit'
								sx={{
									flexGrow: 1,
									backgroundColor: blueGrey[500],
									'&:hover': { backgroundColor: blueGrey[600] },
								}}
							>
								Save
							</Button>
						</Stack>
					</Grid>
				</Box>
			</Modal>
			<Modal
				open={dialogueModalOpen}
				closeAfterTransition
				BackdropComponent={Backdrop}
				BackdropProps={{
					timeout: 1000,
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: blueGrey[50],
						border: '2px solid #000',
						boxShadow: 24,
						p: 4,
					}}
				>
					<Box
						sx={{
							textAlign: 'center',
							fontSize: '1.25em',
							color: blueGrey[900],
						}}
					>
						<Box>There are unsaved changes.</Box>
						<Box>How would you like to proceed?</Box>
						<Stack
							spacing={2}
							direction='row'
							sx={{ marginTop: '3em' }}
						>
							<Button
								variant='contained'
								onClick={handleDialogueModal}
								sx={{
									flexGrow: 1,
									backgroundColor: blueGrey[500],
									'&:hover': { backgroundColor: blueGrey[600] },
								}}
							>
								Cancel & Go Back
							</Button>
							<Button
								variant='contained'
								onClick={discardChanges}
								sx={{
									flexGrow: 1,
									backgroundColor: blueGrey[500],
									'&:hover': { backgroundColor: blueGrey[600] },
								}}
							>
								Discard Changes
							</Button>
						</Stack>
					</Box>
				</Box>
			</Modal>
		</Box>
	);
}
